import { Download, Sparkles } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppUpdate } from "../../hooks/useAppUpdate";

/**
 * "A new version is available" gate for the native apps. Renders nothing on
 * the website and nothing when the installed build is current — see
 * src/native/appUpdate.ts for how required vs optional is decided.
 */
export function AppUpdateModal() {
  const { t } = useTranslation();
  const { update, dismiss } = useAppUpdate();

  const isOpen = Boolean(update);

  // The modal covers the whole app; let the page behind it stop scrolling.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  if (!update) return null;

  const isRequired = update.requirement === "required";

  const handleUpdate = () => {
    // Leaves the WebView: Capacitor hands apps.apple.com off to the system,
    // which opens the App Store app on the listing.
    window.open(update.storeUrl, "_blank");
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-update-title"
      className="fixed inset-0 z-[100] flex items-center justify-center px-5 bg-gray-900/60 backdrop-blur-sm"
      // A required update has nowhere to go, so the backdrop is inert too.
      onClick={isRequired ? undefined : dismiss}
    >
      <div
        className="w-full max-w-sm rounded-3xl bg-white p-7 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white">
          {isRequired ? <Download size={26} /> : <Sparkles size={26} />}
        </div>

        <h2
          id="app-update-title"
          className="mt-5 text-center text-xl font-extrabold tracking-tight text-gray-900"
        >
          {isRequired
            ? t("appUpdate.requiredTitle", "Обновите приложение")
            : t("appUpdate.title", "Доступна новая версия")}
        </h2>

        <p className="mt-2 text-center text-sm leading-relaxed text-gray-500">
          {isRequired
            ? t(
                "appUpdate.requiredMessage",
                "Эта версия приложения больше не поддерживается. Обновитесь, чтобы продолжить.",
              )
            : t(
                "appUpdate.message",
                "Обновите приложение, чтобы получить последние улучшения и исправления.",
              )}
        </p>

        <p className="mt-3 text-center text-xs text-gray-400">
          {t("appUpdate.versions", "Версия {{current}} → {{latest}}", {
            current: update.currentVersion,
            latest: update.latestVersion,
          })}
        </p>

        <button
          type="button"
          onClick={handleUpdate}
          className="mt-6 h-12 w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 font-bold text-white shadow-lg shadow-blue-600/25 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-600/30 active:translate-y-0"
        >
          {t("appUpdate.update", "Обновить")}
        </button>

        {!isRequired && (
          <button
            type="button"
            onClick={dismiss}
            className="mt-2 h-11 w-full rounded-xl text-sm font-semibold text-gray-500 transition hover:bg-gray-50 hover:text-gray-700"
          >
            {t("appUpdate.later", "Позже")}
          </button>
        )}
      </div>
    </div>
  );
}
